import { ElementType } from 'react';
import { MenuItem, SvgIconProps, Typography } from '@mui/material';
import { NavLink, To } from 'react-router-dom';
import { styled, Theme } from '@mui/material/styles';
import { isDarkMode } from '@localstack/services';

export const getSectionItemTextColor = (theme: Theme): string => {
  return isDarkMode(theme) ? theme.palette.text.primary : theme.palette.grey[800];
};

export const getSectionItemBackground = (theme: Theme): string => {
  return isDarkMode(theme) ? theme.palette.background.paper : theme.palette.grey[200];
};

export const SectionHeader = styled('span')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.text.secondary,
  padding: '0 5px',
}));

export const NavbarContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  flexGrow: 1,
}));

export const NavbarSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
}));

type StyledMenuItemProps = React.ComponentProps<typeof MenuItem> & {
  component?: React.ElementType;
  to: To;
  hide?: boolean;
  end?: boolean;
};

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'hide',
})<StyledMenuItemProps>(({ theme, hide }) => ({
  padding: '5px',
  '&:hover': {
    background: getSectionItemBackground(theme),
  },
  '&.active': {
    background: getSectionItemBackground(theme),
  },
  borderRadius: '4px',
  color: getSectionItemTextColor(theme),
  display: hide ? 'none' : 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'flex-start',
}));

const StyledTypography = styled(Typography)(() => ({
  marginLeft: '0.5em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
}));

interface SectionItemProps extends StyledMenuItemProps {
  label: string;
  icon: ElementType<SvgIconProps>;
  isActive?: boolean;
  hide?: boolean;
}

/** Component that renders a reusable menu item with an icon */
export const SectionItem = ({
  to,
  label,
  icon: IconComponent,
  isActive,
  hide = false,
  ...restProps
}: SectionItemProps): JSX.Element => {
  return (
    <StyledMenuItem
      className={isActive ? 'active' : undefined}
      component={NavLink}
      hide={hide}
      to={to}
      tabIndex={0}
      {...restProps}
    >
      <IconComponent sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '18px' }} />
      <StyledTypography variant="inherit">{label}</StyledTypography>
    </StyledMenuItem>
  );
};
